import { useState, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import {
  Search, Upload, Download, Check, AlertTriangle,
  Loader, ChevronDown, X, FileSpreadsheet, User, Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── API helpers ─────────────────────────────────────────────────────────────
const vbApi = {
  search:                  (q)       => api.get('/vacation-balance/search', { params: { q } }),
  updateManual:            (uid, d)  => api.put(`/vacation-balance/${uid}`, d),
  recalculateSeniority:    (uid, fa) => api.post(`/vacation-balance/${uid}/recalculate-seniority`, { fecha_antiguedad: fa }),
  recalculateAll:          ()        => api.post('/vacation-balance/recalculate-all-balances'),
  downloadTemplate:        ()        => api.get('/vacation-balance/template', { responseType: 'blob' }),
  bulkImport:              (file)    => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/vacation-balance/bulk', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// ─── MANUAL SECTION ───────────────────────────────────────────────────────────
function ManualSection() {
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected]   = useState(null);
  const [daysUsed, setDaysUsed]   = useState('');
  const [carryUsed, setCarryUsed] = useState('');
  const [notes, setNotes]         = useState('');
  const [saving, setSaving]       = useState(false);
  const [fechaAnt, setFechaAnt]   = useState('');
  const [recalculating, setRecalculating] = useState(false);
  const [recalcAll, setRecalcAll] = useState(false);
  const [recalcResult, setRecalcResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSearch = useCallback(async (val) => {
    setQuery(val);
    if (val.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await vbApi.search(val);
      setResults(res.data);
    } catch (err) { console.warn('User search error:', err); }
    finally { setSearching(false); }
  }, []);

  const handleSelect = (emp) => {
    setSelected(emp);
    setDaysUsed(String(emp.vacation_used ?? 0));
    setCarryUsed(String(emp.carryover_used ?? 0));
    setFechaAnt(emp.fecha_antiguedad || '');
    setNotes('');
    setResults([]);
    setQuery('');
  };

  const handleRecalcSeniority = async () => {
    if (!selected || !fechaAnt) {
      toast.error('Introduce una fecha de antigüedad válida');
      return;
    }
    setRecalculating(true);
    try {
      const res = await vbApi.recalculateSeniority(selected.id, fechaAnt);
      toast.success(res.data.message);
      setSelected(prev => ({ ...prev, vacation_total: res.data.vacation_total_calculated, fecha_antiguedad: fechaAnt }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al recalcular');
    } finally { setRecalculating(false); }
  };

  const handleRecalcAll = async () => {
    setShowConfirm(false);
    setRecalcAll(true);
    setRecalcResult(null);
    try {
      const res = await vbApi.recalculateAll();
      setRecalcResult(res.data);
      if (res.data.updated_count === 0) {
        toast.info('Ningún saldo ha cambiado — todos los empleados ya tienen el cálculo correcto.');
      } else {
        toast.success(`${res.data.updated_count} empleados actualizados`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al recalcular todos');
    } finally { setRecalcAll(false); }
  };

  const handleSave = async () => {
    if (!selected) return;
    const val = parseFloat(daysUsed);
    if (isNaN(val) || val < 0 || val > 50) {
      toast.error('Introduce un número válido de días (0–50)');
      return;
    }
    setSaving(true);
    try {
      await vbApi.updateManual(selected.id, {
        vacation_used: val,
        carryover_used: carryUsed !== '' ? parseFloat(carryUsed) : undefined,
        notes,
      });
      toast.success(`Balance actualizado para ${selected.first_name} ${selected.last_name}`);
      setSelected(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      {!selected && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            data-testid="manual-search-input"
            type="text"
            value={query}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Buscar por nombre o email..."
            className="w-full pl-9 pr-4 h-10 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          />
          {searching && <Loader className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
          {results.length > 0 && (
            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-lg overflow-hidden">
              {results.map(emp => (
                <button
                  key={emp.id}
                  onClick={() => handleSelect(emp)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 text-left transition-colors"
                  data-testid={`emp-result-${emp.id}`}
                >
                  <div className="h-8 w-8 rounded-full bg-brand/10 text-brand flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {emp.first_name?.[0]}{emp.last_name?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{emp.first_name} {emp.last_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{emp.email}</p>
                  </div>
                  <div className="text-xs text-muted-foreground flex-shrink-0">
                    {emp.vacation_used}d usados / {emp.vacation_total}d total
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Selected employee form */}
      {selected && (
        <div className="border border-border rounded-xl p-4 space-y-4 bg-muted/20" data-testid="manual-form">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-brand/10 text-brand flex items-center justify-center text-sm font-bold">
                {selected.first_name?.[0]}{selected.last_name?.[0]}
              </div>
              <div>
                <p className="font-semibold text-sm">{selected.first_name} {selected.last_name}</p>
                <p className="text-xs text-muted-foreground">{selected.email}</p>
              </div>
            </div>
            <button onClick={() => setSelected(null)} className="text-muted-foreground hover:text-foreground p-1 rounded">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Días de vacaciones usados en 2026</label>
              <div className="flex items-center gap-2">
                <input
                  data-testid="input-vacation-used"
                  type="number" min="0" max="50" step="0.5"
                  value={daysUsed}
                  onChange={e => setDaysUsed(e.target.value)}
                  className="w-full h-9 px-3 rounded-lg border border-border bg-background text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-brand"
                />
                <span className="text-xs text-muted-foreground">días</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Disponibles tras ajuste: <strong>{Math.max(0, (selected.vacation_total + (selected.carryover_days || 0)) - parseFloat(daysUsed || 0)).toFixed(1)}</strong>d
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Días de arrastre usados (opcional)</label>
              <div className="flex items-center gap-2">
                <input
                  data-testid="input-carryover-used"
                  type="number" min="0" max="30" step="0.5"
                  value={carryUsed}
                  onChange={e => setCarryUsed(e.target.value)}
                  className="w-full h-9 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                />
                <span className="text-xs text-muted-foreground">días</span>
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Notas / motivo del ajuste (opcional)</label>
            <input
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Ej: Backfill vacaciones Q1 2026"
              className="w-full h-9 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>

          {/* Seniority recalculation */}
          <div className="border border-dashed border-orange-300 dark:border-orange-700 rounded-xl p-3 space-y-2 bg-orange-50/50 dark:bg-orange-900/10">
            <p className="text-xs font-semibold text-orange-700 dark:text-orange-300 flex items-center gap-1">
              <span>Recalcular por antigüedad</span>
              <span className="font-normal text-orange-600">— nueva incorporación (&lt;1 año)</span>
            </p>
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-1">
                <label className="text-xs text-muted-foreground">Fecha de antigüedad (entrada)</label>
                <input
                  data-testid="input-fecha-antiguedad"
                  type="date"
                  value={fechaAnt}
                  onChange={e => setFechaAnt(e.target.value)}
                  className="w-full h-9 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                />
              </div>
              {fechaAnt && (
                <div className="text-xs text-muted-foreground text-right">
                  <div className="font-semibold text-orange-600">
                    ≈ {(() => {
                      const ant = new Date(fechaAnt + 'T12:00');
                      const today = new Date();
                      const months = (today.getFullYear() - ant.getFullYear()) * 12 + (today.getMonth() - ant.getMonth()) - (today.getDate() < ant.getDate() ? 1 : 0);
                      const years = (today - ant) / (1000 * 60 * 60 * 24 * 365.25);
                      if (years >= 1) return '24 días';
                      return `${Math.max(0, months) * 2} días`;
                    })()}
                  </div>
                  <div className="opacity-70">calculado</div>
                </div>
              )}
              <Button
                data-testid="btn-recalculate-seniority"
                onClick={handleRecalcSeniority}
                disabled={recalculating || !fechaAnt}
                size="sm"
                className="bg-orange-500 hover:bg-orange-600 text-white gap-1 whitespace-nowrap"
              >
                {recalculating ? <Loader className="h-3 w-3 animate-spin" /> : null}
                Recalcular
              </Button>
            </div>
          </div>

          <Button
            data-testid="btn-save-manual"
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-brand hover:bg-brand-hover text-white gap-2"
          >
            {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar ajuste días usados
          </Button>
        </div>
      )}

      {/* Global recalculate by seniority */}
      <div className="mt-4 pt-4 border-t border-border space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs font-semibold">Recalcular TODOS los saldos de vacaciones {new Date().getFullYear()}</p>
            <p className="text-xs text-muted-foreground max-w-lg">
              Usa <strong>fecha_antigüedad</strong> si está guardada, o <strong>fecha_inicio</strong> como alternativa.
              Empleados con ≥ 1 año quedan en 24 días. Nuevos: 2 días por mes completado.
            </p>
          </div>
          <Button
            data-testid="btn-recalculate-all"
            onClick={() => setShowConfirm(true)}
            disabled={recalcAll}
            size="sm"
            className="gap-2 bg-orange-500 hover:bg-orange-600 text-white"
          >
            {recalcAll ? <Loader className="h-3 w-3 animate-spin" /> : null}
            {recalcAll ? 'Recalculando...' : 'Recalcular todos los saldos'}
          </Button>
        </div>

        {/* Confirm dialog */}
        {showConfirm && (
          <div className="p-4 rounded-xl border border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 space-y-3">
            <p className="text-sm font-semibold text-orange-800 dark:text-orange-200">¿Confirmas la recalculación masiva?</p>
            <p className="text-xs text-orange-700 dark:text-orange-300">
              Esto actualizará el campo <strong>vacation_total</strong> en los saldos de vacaciones del año {new Date().getFullYear()} para todos los empleados activos. La operación es reversible manualmente.
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleRecalcAll} className="bg-orange-500 hover:bg-orange-600 text-white">Confirmar</Button>
              <Button size="sm" variant="outline" onClick={() => setShowConfirm(false)}>Cancelar</Button>
            </div>
          </div>
        )}

        {/* Results */}
        {recalcResult && (
          <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Procesados', val: recalcResult.total_processed, cls: 'text-foreground' },
                { label: 'Actualizados', val: recalcResult.updated_count, cls: 'text-green-600 dark:text-green-400' },
                { label: 'Sin cambios', val: recalcResult.kept_unchanged, cls: 'text-muted-foreground' },
                { label: 'Sin fecha', val: recalcResult.skipped_no_date, cls: 'text-orange-500' },
              ].map(({ label, val, cls }) => (
                <div key={label} className="text-center p-2 rounded-lg bg-background border border-border/50">
                  <div className={`text-xl font-bold font-heading ${cls}`}>{val}</div>
                  <div className="text-xs text-muted-foreground">{label}</div>
                </div>
              ))}
            </div>
            {recalcResult.updated?.length > 0 && (
              <div>
                <p className="text-xs font-semibold mb-2 text-green-700 dark:text-green-400">Saldos modificados:</p>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {recalcResult.updated.map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-xs px-3 py-1.5 rounded-lg bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800">
                      <span className="font-medium">{r.user}</span>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <span>({r.source === 'fecha_antiguedad' ? 'antigüedad' : 'inicio'}: {r.date_used})</span>
                        <span className="line-through">{r.old_total}d</span>
                        <span className="text-green-600 font-semibold">→ {r.new_total}d</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {recalcResult.skipped_users?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-orange-600 dark:text-orange-400">
                  {recalcResult.skipped_users.length} empleados sin fecha — no modificados (click para ver)
                </summary>
                <div className="mt-1 text-muted-foreground">{recalcResult.skipped_users.join(', ')}</div>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── BULK SECTION ─────────────────────────────────────────────────────────────
function BulkSection() {
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [result, setResult]           = useState(null);
  const [dragOver, setDragOver]       = useState(false);
  const fileRef = useRef(null);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await vbApi.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_vacaciones_2026.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Plantilla descargada');
    } catch { toast.error('Error al descargar la plantilla'); }
    finally { setDownloading(false); }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setResult(null);
    setUploading(true);
    try {
      const res = await vbApi.bulkImport(file);
      setResult(res.data);
      if (res.data.updated > 0) toast.success(`${res.data.updated} empleados actualizados`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en la importación');
    } finally { setUploading(false); }
  };

  return (
    <div className="space-y-5">
      {/* Step 1: Download template */}
      <div className="flex items-start gap-4 p-4 bg-muted/20 rounded-xl border border-border">
        <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
        <div className="flex-1">
          <p className="text-sm font-medium mb-1">Descarga la plantilla Excel</p>
          <p className="text-xs text-muted-foreground mb-3">
            Contiene todos los empleados activos con su balance actual pre-relleno. Solo tienes que actualizar
            la columna <strong>"Días Usados 2026"</strong> (en amarillo).
          </p>
          <Button
            data-testid="btn-download-template"
            variant="outline"
            onClick={handleDownload}
            disabled={downloading}
            className="gap-2"
          >
            {downloading ? <Loader className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Descargar plantilla ({new Date().getFullYear()})
          </Button>
        </div>
      </div>

      {/* Step 2: Upload filled template */}
      <div className="flex items-start gap-4 p-4 bg-muted/20 rounded-xl border border-border">
        <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
        <div className="flex-1">
          <p className="text-sm font-medium mb-1">Rellena y sube la plantilla</p>
          <p className="text-xs text-muted-foreground mb-3">
            Acepta .xlsx, .xls o .csv. Los días se actualizan solo para los empleados que modifiques.
          </p>

          {/* Drop zone */}
          <div
            data-testid="drop-zone"
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
              dragOver ? 'border-brand bg-brand/5' : 'border-border hover:border-brand/50'
            }`}
            onClick={() => fileRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={e => handleFile(e.target.files[0])}
              data-testid="file-input"
            />
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader className="h-8 w-8 animate-spin text-brand" />
                <p className="text-sm text-muted-foreground">Procesando...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <FileSpreadsheet className="h-10 w-10 text-muted-foreground/50" />
                <p className="text-sm font-medium">Arrastra tu archivo aquí o haz clic para buscar</p>
                <p className="text-xs text-muted-foreground">.xlsx · .xls · .csv</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div
          data-testid="bulk-result"
          className={`rounded-xl p-4 border ${result.updated > 0 ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800' : 'bg-amber-50 dark:bg-amber-900/10 border-amber-200'}`}
        >
          <div className="flex items-center gap-2 mb-2">
            {result.updated > 0
              ? <Check className="h-4 w-4 text-green-600" />
              : <AlertTriangle className="h-4 w-4 text-amber-600" />}
            <span className="font-semibold text-sm">Resultado de la importación</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="bg-white dark:bg-slate-800 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-600">{result.updated}</div>
              <div className="text-xs text-muted-foreground">Actualizados</div>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-amber-500">{result.not_found_count}</div>
              <div className="text-xs text-muted-foreground">No encontrados</div>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-red-500">{result.errors?.length || 0}</div>
              <div className="text-xs text-muted-foreground">Errores</div>
            </div>
          </div>
          {result.not_found_emails?.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-amber-600 font-medium">
                Ver emails no encontrados ({result.not_found_emails.length})
              </summary>
              <div className="mt-2 bg-white dark:bg-slate-800 rounded p-2 font-mono text-[10px] max-h-32 overflow-y-auto">
                {result.not_found_emails.map((e) => <div key={e}>{e}</div>)}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MAIN PAGE ─────────────────────────────────────────────────────────────────
export default function VacationBalanceAdmin() {
  const [tab, setTab] = useState('manual');

  return (
    <div className="max-w-3xl mx-auto space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-heading">Ajuste de Vacaciones 2026</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Registra los días de vacaciones ya disfrutados por los empleados durante Q1 2026, antes de poner en producción.
        </p>
      </div>

      {/* Warning banner */}
      <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700">
        <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800 dark:text-amber-200">
          <strong>Operación de producción:</strong> Este ajuste sobreescribe los días usados actuales.
          Asegúrate de que los datos son correctos antes de guardar. Todos los cambios quedan registrados en el log de auditoría.
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-muted rounded-xl w-fit">
        {[
          { id: 'manual', label: 'Ajuste individual', icon: User },
          { id: 'bulk',   label: 'Importación masiva', icon: FileSpreadsheet },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`tab-${id}`}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === id
                ? 'bg-white dark:bg-slate-800 shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
        {tab === 'manual' ? <ManualSection /> : <BulkSection />}
      </div>
    </div>
  );
}
